import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, withQuery, money, quantity, date } from '../api.js';
import Lookup from '../components/Lookup.jsx';
import ProductLookup from '../components/ProductLookup.jsx';

export const OFFER_STATUS_LABEL = { open: 'отворена', accepted: 'приета', rejected: 'отказана' };
const OFFER_STATUS_CLASS = { open: 'badge warn', accepted: 'badge ok', rejected: 'badge muted' };

/*
 * Оферти към клиенти (29.10.2026, Калин).
 *
 * Стоките и услугите се избират като в продажбата; предложената цена е
 * същата (ценово правило → последна към клиента → картон), но е само
 * начало — операторът пише каквато цена реши, без или с ДДС. До нея
 * стоят доставната и едро, за да се вижда маржът. На клиента се печата
 * само клиентската цена.
 */

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
function cenaVPole(x) {
  const n = Math.round((Number(x) + Number.EPSILON) * 10000) / 10000;
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-6 ? n.toFixed(2) : String(n);
}
const chislo = (v) => Number(String(v ?? '').replace(',', '.'));
const num = (v) => (v == null ? null : Number(v));
let kluch = 0;

export default function Offers() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get(withQuery('/api/offers', status ? { status } : {}))
      .then(setRows)
      .catch((e) => setError(e.message));
  }, [status]);

  return (
    <>
      <div className="page-head">
        <h1>Оферти</h1>
        <div className="spacer" />
        <button type="button" className="primary" onClick={() => navigate('/offers/new')}>Нова оферта</button>
      </div>
      <div className="toolbar">
        {[['open', 'Отворени'], ['accepted', 'Приети'], ['rejected', 'Отказани'], ['', 'Всички']]
          .map(([k, l]) => (
            <button key={k} type="button" className={status === k ? 'tab active' : 'tab'}
              onClick={() => setStatus(k)}>{l}</button>
          ))}
      </div>
      {error && <div className="error">{error}</div>}
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 60 }}>№</th>
              <th style={{ width: 95 }}>Дата</th>
              <th>Клиент</th>
              <th>Статус</th>
              <th className="num">Редове</th>
              <th className="num">Без ДДС</th>
              <th className="num">С ДДС</th>
              <th className="num">Печалба</th>
              <th>Валидна до</th>
              <th>Изготвил</th>
            </tr>
          </thead>
          <tbody>
            {!rows && !error && <tr><td colSpan={10} className="empty">Зареждане…</td></tr>}
            {rows?.map((o) => (
              <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/offers/${o.id}`)}>
                <td><Link to={`/offers/${o.id}`}>{o.offer_no}</Link></td>
                <td>{date(o.offer_date)}</td>
                <td>{o.partner_name}</td>
                <td><span className={OFFER_STATUS_CLASS[o.status]}>{OFFER_STATUS_LABEL[o.status]}</span></td>
                <td className="num">{o.line_count ?? 0}</td>
                <td className="num">{money(o.total_net ?? 0)}</td>
                <td className="num">{money(o.total_gross ?? 0)}</td>
                <td className="num">{money(Number(o.total_net ?? 0) - Number(o.total_cost ?? 0))}</td>
                <td>{o.valid_until ? date(o.valid_until) : ''}</td>
                <td className="muted">{o.created_by_name}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={10} className="empty">Няма оферти</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Текстът за клиента по подразбиране — бележката на стоката, иначе името. */
const osnovenText = (p) => (p.product_note ?? '').trim() || p.name;

function redOtServera(i) {
  return {
    key: ++kluch,
    productId: Number(i.product_id),
    name: i.name,
    description: i.description ?? osnovenText(i),
    osnoven: osnovenText(i),
    capacity: i.capacity ?? null,
    unit: i.unit,
    quantity: String(Number(i.quantity)),
    price: cenaVPole(i.price),
    gross: null,
    vat: Number(i.vat_rate ?? 20),
    purchase: num(i.current_purchase_price ?? i.purchase_price),
    wholesale: num(i.wholesale_price),
    retail: num(i.retail_price),
    nalichnost: num(i.nalichnost),
    isStocked: Number(i.is_stocked) === 1,
    note: i.note ?? '',
  };
}

/** Нова оферта (/offers/new) и редакция на съществуваща (/offers/:id). */
export function OfferView() {
  const { id } = useParams();
  const nova = id === 'new';
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [partner, setPartner] = useState(null);
  const [nivo, setNivo] = useState('retail');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [vest, setVest] = useState(null);

  function priemi(d) {
    setO(d);
    setPartner({ id: Number(d.partner_id), name: d.partner_name });
    setValidUntil(d.valid_until ? String(d.valid_until).slice(0, 10) : '');
    setNote(d.note ?? '');
    setLines(d.items.map(redOtServera));
    setDirty(false);
  }

  useEffect(() => {
    if (nova) {
      setO(null); setPartner(null); setLines([]); setNote(''); setValidUntil(''); setDirty(false);
      return;
    }
    api.get(`/api/offers/${id}`).then(priemi).catch((e) => setError(e.message));
  }, [id]);

  useEffect(() => {
    if (!partner?.id) return;
    api.get(`/api/partners/${partner.id}`)
      .then((p) => setNivo(p.default_price_level === 'wholesale' ? 'wholesale' : 'retail'))
      .catch(() => {});
  }, [partner?.id]);

  // Незаписани промени — питане преди затваряне на раздела.
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  if (!nova && error && !o) return <div className="error">{error}</div>;
  if (!nova && !o) return <div className="card"><div className="empty">Зареждане…</div></div>;

  const otvorena = nova || o.status === 'open';
  const promeni = (fn) => { setLines(fn); setDirty(true); setVest(null); };
  const patch = (key, p) => promeni((s) => s.map((l) => (l.key === key ? { ...l, ...p } : l)));

  async function dobavi(pid) {
    if (!pid) return;
    setError(null);
    try {
      const [p, info] = await Promise.all([
        api.get(`/api/offers/product/${pid}`),
        api.get(withQuery(`/api/products/${pid}/sale-info`, {
          partnerId: partner?.id || undefined, priceLevel: nivo,
        })).catch(() => null),
      ]);
      const predlozhena = info?.prices?.suggested
        ?? (nivo === 'wholesale' ? p.wholesale_price ?? p.retail_price : p.retail_price ?? p.wholesale_price)
        ?? 0;
      promeni((s) => [...s, {
        key: ++kluch,
        productId: Number(p.id),
        name: p.name,
        description: osnovenText(p),
        osnoven: osnovenText(p),
        capacity: p.capacity ?? null,
        unit: p.unit,
        quantity: '1',
        price: cenaVPole(predlozhena),
        gross: null,
        vat: Number(p.vat_rate ?? 20),
        purchase: num(p.purchase_price),
        wholesale: num(p.wholesale_price),
        retail: num(p.retail_price),
        nalichnost: num(p.nalichnost),
        isStocked: Number(p.is_stocked) === 1,
        note: '',
      }]);
    } catch (e) { setError(e.message); }
  }

  async function zapishi() {
    if (!partner?.id) { setError('Изберете клиент'); return null; }
    if (!lines.length) { setError('Добавете поне една стока или услуга'); return null; }
    const loshi = lines.findIndex((l) => !(chislo(l.quantity) > 0) || !(chislo(l.price) >= 0) || l.price === '');
    if (loshi >= 0) { setError(`Ред ${loshi + 1}: количеството и цената трябва да са числа`); return null; }
    setBusy(true); setError(null);
    try {
      const telo = {
        partnerId: partner.id,
        validUntil: validUntil || null,
        note: note.trim() || null,
        lines: lines.map((l) => ({
          productId: l.productId,
          description: l.description.trim() || null,
          quantity: chislo(l.quantity),
          price: chislo(l.price),
          note: l.note.trim() || null,
        })),
      };
      const d = nova ? await api.post('/api/offers', telo) : await api.put(`/api/offers/${id}`, telo);
      setDirty(false);
      if (nova) navigate(`/offers/${d.id}`, { replace: true });
      else priemi(d);
      setVest('Записано');
      setBusy(false);
      return d;
    } catch (e) { setError(e.message); setBusy(false); return null; }
  }

  async function status(s) {
    setBusy(true); setError(null);
    try {
      if (dirty && !(await zapishi())) { setBusy(false); return; }
      priemi(await api.post(`/api/offers/${id}/status`, { status: s }));
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function pechat() {
    let oid = id;
    if (nova || dirty) {
      const d = await zapishi();
      if (!d) return;
      oid = d.id;
    }
    window.open(`/print/offer/${oid}`, '_blank');
  }

  async function iztrii() {
    if (!window.confirm(`Да се изтрие ли оферта № ${o.offer_no}?`)) return;
    try {
      await api.del(`/api/offers/${id}`);
      navigate('/offers');
    } catch (e) { setError(e.message); }
  }

  const sbor = lines.reduce((a, l) => {
    const q = chislo(l.quantity) || 0;
    const net = round2(q * (chislo(l.price) || 0));
    a.net += net;
    a.vat += round2(net * l.vat / 100);
    a.cost += q * (l.purchase ?? 0);
    return a;
  }, { net: 0, vat: 0, cost: 0 });
  const pechalba = sbor.net - sbor.cost;

  return (
    <>
      <div className="page-head">
        <h1>{nova ? 'Нова оферта' : `Оферта № ${o.offer_no}`}</h1>
        {!nova && <span className={OFFER_STATUS_CLASS[o.status]}>{OFFER_STATUS_LABEL[o.status]}</span>}
        <div className="spacer" />
        <button type="button" onClick={() => navigate('/offers')}>Всички оферти</button>
        <button type="button" onClick={pechat} disabled={busy || !lines.length}>Печат</button>
      </div>
      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="toolbar">
          <label className="field" style={{ minWidth: 300, flex: 1 }}>
            Клиент
            {otvorena
              ? <Lookup url="/api/partners" value={partner?.id ?? null} label={partner?.name ?? ''}
                  onPick={(pid, name) => { setPartner(pid ? { id: pid, name } : null); setDirty(true); }} />
              : <strong><Link to={`/partners/${o.partner_id}`}>{o.partner_name}</Link></strong>}
          </label>
          {!nova && (
            <label className="field">
              Дата
              <span>{date(o.offer_date)}</span>
            </label>
          )}
          <label className="field">
            Валидна до
            <input type="date" value={validUntil} disabled={!otvorena}
              onChange={(e) => { setValidUntil(e.target.value); setDirty(true); }} />
          </label>
          <label className="field">
            Цени по
            <select value={nivo} disabled={!otvorena} onChange={(e) => setNivo(e.target.value)}
              title="Само за предложената цена на новите редове">
              <option value="retail">дребно</option>
              <option value="wholesale">едро</option>
            </select>
          </label>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="compact">
            <thead>
              <tr>
                <th style={{ width: 30 }}>№</th>
                <th>Стока / услуга</th>
                <th className="num">Нал.</th>
                <th className="num" style={{ width: 80 }}>Кол.</th>
                <th className="num" title="Среднопретеглена от партидите с наличност, без ДДС">Доставна</th>
                <th className="num" title="От картона на стоката, без ДДС">Едро</th>
                <th className="num" style={{ width: 100 }}>Клиентска<br />без ДДС</th>
                <th className="num" style={{ width: 100 }}>Клиентска<br />с ДДС</th>
                <th className="num">Стойност<br />без ДДС</th>
                <th className="num" title="Печалба ÷ цена без ДДС">Марж</th>
                {otvorena && <th style={{ width: 28 }} />}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const cena = chislo(l.price) || 0;
                const q = chislo(l.quantity) || 0;
                const brutoPole = l.gross ?? (l.price === '' ? '' : cenaVPole(round2(cena * (1 + l.vat / 100))));
                const marzh = cena > 0 && l.purchase != null ? ((cena - l.purchase) / cena) * 100 : null;
                const podDostavna = l.purchase != null && cena < l.purchase;
                const slozhi = (v) => patch(l.key, { price: cenaVPole(v), gross: null });
                return (
                  <tr key={l.key}>
                    <td className="muted">{i + 1}</td>
                    <td style={{ minWidth: 220 }}>
                      {otvorena
                        ? <input value={l.description} style={{ width: '100%' }} maxLength={255}
                            title={`в номенклатурата: ${l.name}`}
                            onChange={(e) => patch(l.key, { description: e.target.value })} />
                        : <>{l.description}</>}
                      <div className="muted" style={{ fontSize: 11 }}>
                        <Link to={`/products/${l.productId}`} tabIndex={-1}>{l.name}</Link>
                        {l.capacity && <> · {l.capacity}</>}
                        {l.description !== l.osnoven && (
                          <> · текстът е сменен{' '}
                            <button type="button" className="link" style={{ fontSize: 11 }} tabIndex={-1}
                              title={l.osnoven} onClick={() => patch(l.key, { description: l.osnoven })}>
                              върни от стоката
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="num muted">{l.isStocked && l.nalichnost != null ? quantity(l.nalichnost) : ''}</td>
                    <td className="num">
                      {otvorena
                        ? <input className="num" style={{ width: 64 }} value={l.quantity} inputMode="decimal"
                            onChange={(e) => patch(l.key, { quantity: e.target.value })} />
                        : quantity(l.quantity)}
                      <span className="muted"> {l.unit}</span>
                    </td>
                    <td className="num">
                      {l.purchase != null
                        ? (otvorena
                            ? <button type="button" className="link" title="сложи като клиентска цена"
                                onClick={() => slozhi(l.purchase)}>{money(l.purchase)}</button>
                            : money(l.purchase))
                        : <span className="muted">—</span>}
                    </td>
                    <td className="num">
                      {l.wholesale != null
                        ? (otvorena
                            ? <button type="button" className="link" title="сложи като клиентска цена"
                                onClick={() => slozhi(l.wholesale)}>{money(l.wholesale)}</button>
                            : money(l.wholesale))
                        : <span className="muted">—</span>}
                      {l.retail != null && (
                        <div className="muted" style={{ fontSize: 11 }}>
                          дребно{' '}
                          {otvorena
                            ? <button type="button" className="link" style={{ fontSize: 11 }}
                                title="сложи като клиентска цена" onClick={() => slozhi(l.retail)}>
                                {money(l.retail)}
                              </button>
                            : money(l.retail)}
                        </div>
                      )}
                    </td>
                    <td className="num">
                      {otvorena
                        ? <input className="num" style={{ width: 84, fontWeight: 600 }} value={l.price}
                            inputMode="decimal"
                            onChange={(e) => patch(l.key, { price: e.target.value, gross: null })} />
                        : money(l.price)}
                    </td>
                    <td className="num">
                      {otvorena
                        ? <input className="num" style={{ width: 84 }} value={brutoPole} inputMode="decimal"
                            onChange={(e) => {
                              const v = e.target.value;
                              patch(l.key, {
                                gross: v,
                                price: v === '' ? '' : String(Math.round((chislo(v) / (1 + l.vat / 100)) * 10000) / 10000),
                              });
                            }} />
                        : money(round2(cena * (1 + l.vat / 100)))}
                    </td>
                    <td className="num">{money(round2(q * cena))}</td>
                    <td className="num" style={podDostavna ? { color: 'var(--danger, #b91c1c)', fontWeight: 600 } : undefined}
                      title={podDostavna ? 'Под доставната цена' : undefined}>
                      {marzh != null ? `${marzh.toFixed(1)}%` : ''}
                    </td>
                    {otvorena && (
                      <td>
                        <button type="button" className="link" title="махни реда"
                          onClick={() => promeni((s) => s.filter((x) => x.key !== l.key))}>×</button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {lines.length === 0 && (
                <tr><td colSpan={11} className="empty">Добавете стоки и услуги отдолу</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {otvorena && (
          <div className="toolbar" style={{ marginTop: 8 }}>
            <label className="field" style={{ minWidth: 340, flex: 1 }}>
              Добави стока или услуга
              <ProductLookup value={null} label="" placeholder="име, код или бърз код"
                onPick={(pid) => dobavi(pid)} />
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-start' }}>
          <label className="field" style={{ flex: 1, minWidth: 260 }}>
            Бележка към клиента (излиза на печат)
            <textarea rows={3} value={note} disabled={!otvorena} maxLength={1000}
              onChange={(e) => { setNote(e.target.value); setDirty(true); }} />
          </label>
          <table className="compact" style={{ width: 'auto', minWidth: 260 }}>
            <tbody>
              <tr><td>Без ДДС</td><td className="num">{money(sbor.net)}</td></tr>
              <tr><td>ДДС</td><td className="num">{money(sbor.vat)}</td></tr>
              <tr><th>Общо с ДДС</th><th className="num">{money(sbor.net + sbor.vat)}</th></tr>
              <tr className="muted"><td>Доставна стойност</td><td className="num">{money(sbor.cost)}</td></tr>
              <tr className="muted">
                <td>Печалба</td>
                <td className="num">
                  {money(pechalba)}
                  {sbor.net > 0 && ` · ${((pechalba / sbor.net) * 100).toFixed(1)}%`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="toolbar" style={{ marginTop: 12 }}>
          {!nova && o.status === 'open' && (
            <button type="button" onClick={iztrii} disabled={busy}>Изтрий</button>
          )}
          <div className="spacer" />
          {vest && !dirty && <span className="badge ok">{vest}</span>}
          {otvorena && (
            <button type="button" className={dirty || nova ? 'primary' : ''} onClick={zapishi}
              disabled={busy || (!dirty && !nova)}>
              Запиши
            </button>
          )}
          {!nova && o.status === 'open' && (
            <>
              <button type="button" disabled={busy} onClick={() => status('rejected')}>Отказана</button>
              <button type="button" disabled={busy} onClick={() => status('accepted')}>Приета</button>
            </>
          )}
          {!nova && o.status !== 'open' && (
            <>
              <span className="muted">
                {OFFER_STATUS_LABEL[o.status]} на {date(o.closed_at)} от {o.closed_by_name}
              </span>
              <button type="button" disabled={busy} onClick={() => status('open')}>Отвори отново</button>
            </>
          )}
        </div>
        {!nova && (
          <p className="hint">
            Изготвена {date(o.created_at)} от {o.created_by_name}
            {o.updated_at && <> · последна корекция {date(o.updated_at)} от {o.updated_by_name}</>}
            {' '}· Доставната и едро са само за вас — на печат излиза клиентската цена.
          </p>
        )}
      </div>
    </>
  );
}

/** Печатен вид за клиента — само клиентските цени. */
export function PrintOffer() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    Promise.all([api.get(`/api/offers/${id}`), api.get('/api/settings')])
      .then(([d, s]) => { setO(d); setCompany(s.company ?? {}); })
      .catch((e) => setError(e.message));
  }, [id]);
  if (error) return <div className="error" style={{ margin: 20 }}>{error}</div>;
  if (!o || !company) return <div className="empty">Зареждане…</div>;

  const redove = o.items.map((it) => {
    const net = round2(Number(it.quantity) * Number(it.price));
    return { ...it, net, vat: round2(net * Number(it.vat_rate) / 100) };
  });
  const net = redove.reduce((s, r) => s + r.net, 0);
  const vat = redove.reduce((s, r) => s + r.vat, 0);
  const stavki = [...new Set(redove.map((r) => Number(r.vat_rate)))];
  // Колоните за количество и стойност — само ако някъде има повече от една
  // бройка; иначе офертата е списък цени, като образеца на Калин.
  const sKolichestvo = redove.some((r) => Number(r.quantity) !== 1);
  const sKapacitet = redove.some((r) => r.capacity);

  return (
    <>
      <div className="print-toolbar">
        <button className="primary" onClick={() => window.print()}>Печат</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => window.close()}>Затвори</button>
      </div>
      <div className="print-page">
        <div className="print-letterhead">
          <div>
            <img src="/brand/dp-logo.jpg" alt="Доктор Принтер" className="print-logo" />
          </div>
          <div className="print-contacts">
            <div>гр. Варна, ул. Батак 7</div>
            <div>тел. 052 989 334, 052 310 520, 0878 727 397</div>
            <div>Teams: doctor_printer</div>
            <div>office@doctorprinter.bg · www.doctorprinter.bg</div>
          </div>
        </div>

        <div className="oferta-lenta"><span /><span /><span /><span /></div>

        <div className="print-title">
          <h1>ОФЕРТА</h1>
          <div className="sub">№ {o.offer_no} / {date(o.offer_date)}</div>
        </div>

        <div className="print-head">
          <div className="party">
            <h2>До</h2>
            <div className="name">{o.partner_name}</div>
            <Row k="ЕИК" v={o.partner_eik} />
            <Row k="ДДС №" v={o.partner_vat} />
            <Row k="Адрес" v={[o.partner_city, o.partner_address].filter(Boolean).join(', ')} />
            <Row k="МОЛ" v={o.partner_mol} />
          </div>
          <div className="party">
            <h2>От</h2>
            <div className="name">{company.name}</div>
            <Row k="ЕИК" v={company.eik} />
            <Row k="ДДС №" v={company.vat_number} />
            <Row k="Адрес" v={[company.city, company.address].filter(Boolean).join(', ')} />
          </div>
        </div>

        <table className="print-lines oferta">
          <thead>
            <tr>
              <th style={{ width: '8mm' }}>№</th>
              <th>Наименование</th>
              {sKapacitet && <th style={{ width: '30mm' }}>Капацитет</th>}
              {sKolichestvo && <th className="num" style={{ width: '16mm' }}>Кол.</th>}
              <th className="num" style={{ width: '26mm' }}>Цена</th>
              {sKolichestvo && <th className="num" style={{ width: '26mm' }}>Стойност</th>}
            </tr>
          </thead>
          <tbody>
            {redove.map((it) => (
              <tr key={it.id}>
                <td>{it.line_no}</td>
                <td>
                  {it.description ?? osnovenText(it)}
                  {it.note && <div className="line-note">{it.note}</div>}
                </td>
                {sKapacitet && <td>{it.capacity ?? '—'}</td>}
                {sKolichestvo && <td className="num">{quantity(it.quantity)} {it.unit}</td>}
                <td className="num"><strong>{money(it.price)}</strong></td>
                {sKolichestvo && <td className="num">{money(it.net)}</td>}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="print-totals">
          <div><span>Общо без ДДС</span><span>{money(net)}</span></div>
          <div><span>ДДС {stavki.length === 1 ? `${stavki[0]}%` : ''}</span><span>{money(vat)}</span></div>
          <div className="grand"><span>Общо с ДДС</span><span>{money(net + vat)}</span></div>
        </div>
        <p style={{ textAlign: 'center', fontWeight: 600, margin: '3mm 0' }}>Всички цени са без включен ДДС</p>

        <div className="print-meta">
          {o.valid_until && <div><span className="k">Офертата е валидна до: </span>{date(o.valid_until)}</div>}
          {o.note && <div style={{ whiteSpace: 'pre-wrap' }}>{o.note}</div>}
        </div>

        <div className="signatures">
          <div><div className="line" />Изготвил: {o.created_by_name ?? ''}</div>
        </div>
      </div>
    </>
  );
}

function Row({ k, v }) {
  if (!v) return null;
  return <div className="row"><span className="k">{k}:</span><span>{v}</span></div>;
}
